import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    surface2: string;
    accent: string;
    accent2: string;
    accent3: string;
    glow: string;
    textDim: string;
    hexColor: string;
    tagLangColor: string;
    tagLangBg: string;
    tagColor: string;
    tagBg: string;
    heroGradient: string;
  }
  interface PaletteOptions {
    surface2?: string;
    accent?: string;
    accent2?: string;
    accent3?: string;
    glow?: string;
    textDim?: string;
    hexColor?: string;
    tagLangColor?: string;
    tagLangBg?: string;
    tagColor?: string;
    tagBg?: string;
    heroGradient?: string;
  }
}

const sharedOptions: ThemeOptions = {
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: "'Inter', system-ui, sans-serif",
    h1: { fontWeight: 700, letterSpacing: "-0.04em" },
    h2: {
      fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
      fontWeight: 700,
      letterSpacing: "-0.03em"
    },
    h3: { fontSize: "1.75rem", fontWeight: 650, letterSpacing: "-0.02em" },
    h4: { fontSize: "1.75rem", fontWeight: 650, letterSpacing: "-0.02em" },
    h5: { fontSize: "1.25rem", fontWeight: 600 },
    h6: { fontSize: "1.125rem", fontWeight: 600 },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { textTransform: "none", fontWeight: 600 }
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            outline: "2px solid var(--focus-ring)",
            outlineOffset: 2
          }
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableFocusRipple: true },
      styleOverrides: {
        root: {
          minHeight: 44,
          paddingInline: 18,
          transition:
            "background-color 160ms, border-color 160ms, box-shadow 160ms"
        },
        sizeSmall: {
          minHeight: 36,
          paddingInline: 12,
          "@media (pointer: coarse)": { minHeight: 44 }
        },
        outlined: ({ theme }) => ({
          borderColor: theme.palette.mode === "dark" ? "#8888a0" : "#78788e"
        })
      }
    },
    MuiIconButton: {
      defaultProps: { disableFocusRipple: true },
      styleOverrides: { root: { minWidth: 44, minHeight: 44 } }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.mode === "dark" ? "#72728a" : "#78788e"
        })
      }
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiTooltip: { defaultProps: { arrow: true } }
  }
};

export const darkTheme: Theme = createTheme({
  ...sharedOptions,
  palette: {
    mode: "dark",
    primary: {
      main: "#a855f7",
      light: "#c084fc",
      dark: "#7c3aed",
      contrastText: "#0a0a0f"
    },
    secondary: {
      main: "#c084fc",
      light: "#d8b4fe",
      dark: "#a855f7",
      contrastText: "#0a0a0f"
    },
    background: {
      default: "#0a0a0f",
      paper: "#12121a"
    },
    text: {
      primary: "#e4e4ef",
      secondary: "#8888a0"
    },
    divider: "#2a2a3a",
    surface2: "#1a1a26",
    accent: "#a855f7",
    accent2: "#c084fc",
    accent3: "#7c3aed",
    glow: "rgba(168, 85, 247, 0.15)",
    textDim: "#8888a0",
    hexColor: "168, 85, 247",
    tagLangColor: "#c084fc",
    tagLangBg: "rgba(124, 58, 237, 0.12)",
    tagColor: "#c084fc",
    tagBg: "rgba(192, 132, 252, 0.1)",
    heroGradient:
      "linear-gradient(135deg, #e4e4ef 0%, #a855f7 50%, #c084fc 100%)"
  }
});

export const lightTheme: Theme = createTheme({
  ...sharedOptions,
  palette: {
    mode: "light",
    primary: {
      main: "#7c3aed",
      light: "#a855f7",
      dark: "#6d28d9",
      contrastText: "#ffffff"
    },
    secondary: {
      main: "#7c3aed",
      light: "#a855f7",
      dark: "#6d28d9",
      contrastText: "#ffffff"
    },
    background: {
      default: "#f5f5fa",
      paper: "#ffffff"
    },
    text: {
      primary: "#1a1a2e",
      secondary: "#555570"
    },
    divider: "#d0d0e0",
    surface2: "#eeeef5",
    accent: "#7c3aed",
    accent2: "#a855f7",
    accent3: "#6d28d9",
    glow: "rgba(124, 58, 237, 0.12)",
    textDim: "#555570",
    hexColor: "124, 58, 237",
    tagLangColor: "#6d28d9",
    tagLangBg: "rgba(109, 40, 217, 0.1)",
    tagColor: "#7c3aed",
    tagBg: "rgba(124, 58, 237, 0.08)",
    heroGradient:
      "linear-gradient(135deg, #1a1a2e 0%, #7c3aed 50%, #a855f7 100%)"
  }
});
